/**
 * The three bases of a baseball diamond, second at the top, third on the left
 * and first on the right, as a fan reads a scorebug (ISSUE-1091). An occupied
 * base is filled with the theme's primary color, an empty one is an outline.
 * Theme tokens only, never a team color. Decorative: the server's sentence
 * carries the same facts for a screen reader.
 */
import { cn } from "@sportsmet/ui";
import type { ReactNode } from "react";

export type typeBasesDiamondProps = {
  isFirstOccupied: boolean;
  isSecondOccupied: boolean;
  isThirdOccupied: boolean;
  size: "row" | "panel";
};

const HALF_DIAGONAL = 4;

/** A diamond centered on (cx, cy) with a half diagonal of 4 in the 24 x 16 viewBox. */
const diamondPoints = (cx: number, cy: number): string =>
  [
    `${cx},${cy - HALF_DIAGONAL}`,
    `${cx + HALF_DIAGONAL},${cy}`,
    `${cx},${cy + HALF_DIAGONAL}`,
    `${cx - HALF_DIAGONAL},${cy}`,
  ].join(" ");

const BASES = [
  { key: "second", cx: 12, cy: 5 },
  { key: "third", cx: 6, cy: 11 },
  { key: "first", cx: 18, cy: 11 },
] as const;

export const BasesDiamond = (props: typeBasesDiamondProps): ReactNode => {
  const isOccupiedByKey = {
    first: props.isFirstOccupied,
    second: props.isSecondOccupied,
    third: props.isThirdOccupied,
  };
  return (
    <svg
      viewBox="0 0 24 16"
      aria-hidden="true"
      data-testid="bases-diamond"
      className={cn("shrink-0", props.size === "row" ? "h-4 w-6" : "h-12 w-18")}
    >
      {BASES.map((base) => {
        const isOccupied = isOccupiedByKey[base.key];
        return (
          <polygon
            key={base.key}
            points={diamondPoints(base.cx, base.cy)}
            data-testid={`base-${base.key}`}
            data-occupied={isOccupied ? "true" : "false"}
            strokeWidth={1.5}
            strokeLinejoin="round"
            className={
              isOccupied
                ? "fill-current stroke-current text-primary"
                : "fill-none stroke-current text-ink-tertiary"
            }
          />
        );
      })}
    </svg>
  );
};
